import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { XCircle } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import AddExpenseModal from '../Engineer/components/AddExpenseModal';
import {
    cancelLegalCaseExpense,
    createLegalCaseExpenses
} from '../../services/api/implementation/impl';
import { formatRupees } from './budget';
import {
    canCancelCaseExpense,
    canEditLegalCase,
    caseExpenseStatus,
    caseExpenseTotals
} from './legalCase';
import { storageFileName } from './legalRecords';

const formatDate = (value) =>
    value
        ? new Date(value).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
          })
        : '---';

const Links = ({ row }) => {
    const links = [
        row.bill?.url && { href: row.bill.url, label: 'Bill', title: row.bill.name },
        row.payment_link && { href: row.payment_link, label: 'Pay link' }
    ].filter(Boolean);
    if (links.length === 0) return '---';
    return (
        <span className="flex gap-3">
            {links.map((link) => (
                <a
                    key={link.label}
                    href={link.href}
                    title={link.title}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#346C75] underline">
                    {link.label}
                </a>
            ))}
        </span>
    );
};

/**
 * The Expenses tab of a court case. A case has no subtasks, so its expenses sit
 * on the case and go straight to the Accounts queue (no draft, no approval),
 * where Accounts pays them. They never count against a budget.
 */
const LegalCaseExpensesPanel = ({ task }) => {
    const queryClient = useQueryClient();
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [cancelling, setCancelling] = useState(null);

    const refresh = () => queryClient.invalidateQueries(['managementTask']);

    const cancelMutation = useMutation({
        mutationFn: (row) => cancelLegalCaseExpense(task.id, row.id),
        onSuccess: () => {
            toast.success('Expense cancelled');
            setCancelling(null);
            refresh();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to cancel expense');
        }
    });

    const rows = task?.legalCase?.expenses || [];
    const totals = caseExpenseTotals(rows);
    const canAdd = canEditLegalCase(task);
    const busy = cancelMutation.isPending;

    return (
        <>
            {isAddOpen &&
                createPortal(
                    <Modal width={30} name="Add case expense" onClose={() => setIsAddOpen(false)}>
                        <AddExpenseModal
                            draftOnly
                            wholeRupees
                            hint="Sent to Accounts for payment as soon as you submit."
                            storeName={(file) => storageFileName(task.id, file.name)}
                            submit={(expenses) => createLegalCaseExpenses(task.id, expenses)}
                            onSuccess={refresh}
                            onClose={() => setIsAddOpen(false)}
                        />
                    </Modal>,
                    document.body
                )}
            {cancelling && (
                <Modal
                    name="Cancel expense"
                    width={40}
                    onClose={busy ? () => {} : () => setCancelling(null)}
                    hideClose={busy}>
                    <div className="space-y-3">
                        <p className="text-base text-gray-800">
                            Cancel the{' '}
                            <span className="font-semibold">{formatRupees(cancelling.amount)}</span>{' '}
                            expense &lsquo;{cancelling.note}&rsquo;?
                        </p>
                        <p className="text-sm text-gray-600">
                            It is taken out of the Accounts queue. The case keeps it, marked Cancelled.
                        </p>
                    </div>
                    <div className="w-full flex justify-end items-center space-x-4 mt-8">
                        <Button
                            type="button"
                            onClick={() => setCancelling(null)}
                            displayType="secondary"
                            disabled={busy}>
                            Keep it
                        </Button>
                        <Button
                            type="button"
                            onClick={() => cancelMutation.mutate(cancelling)}
                            displayType="primary-red"
                            disabled={busy}>
                            {busy ? 'Cancelling...' : 'Cancel expense'}
                        </Button>
                    </div>
                </Modal>
            )}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">Expenses</h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Fees and costs paid on this case. Each one goes to Accounts for payment.
                        </p>
                        {rows.length > 0 && (
                            <p className="mt-2 text-sm text-gray-700">
                                <span className="font-semibold">{formatRupees(totals.withAccounts)}</span> with
                                Accounts · <span className="font-semibold">{formatRupees(totals.paid)}</span> paid
                            </p>
                        )}
                    </div>
                    {canAdd && (
                        <button
                            type="button"
                            className="bg-[#346C75] text-white px-4 py-2 rounded hover:bg-[#2a565e]"
                            onClick={() => setIsAddOpen(true)}>
                            + Add expense
                        </button>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-left">
                        <thead className="bg-gray-50 text-gray-600">
                            <tr>
                                <th className="px-5 py-3 font-medium text-sm">Date</th>
                                <th className="px-5 py-3 font-medium text-sm">Amount</th>
                                <th className="px-5 py-3 font-medium text-sm">Note</th>
                                <th className="px-5 py-3 font-medium text-sm">Added by</th>
                                <th className="px-5 py-3 font-medium text-sm">Bill / link</th>
                                <th className="px-5 py-3 font-medium text-sm">Status</th>
                                {canAdd && <th className="px-5 py-3 font-medium text-sm">Action</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {rows.length > 0 ? (
                                rows.map((row) => {
                                    const status = caseExpenseStatus(row);
                                    return (
                                        <tr key={row.id} className="hover:bg-gray-50 align-top">
                                            <td className="px-5 py-3 text-sm text-gray-700 whitespace-nowrap">
                                                {formatDate(row.createdAt)}
                                            </td>
                                            <td className="px-5 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">
                                                {formatRupees(row.amount)}
                                            </td>
                                            <td className="px-5 py-3 text-sm text-gray-700 whitespace-pre-wrap">
                                                {row.note || '---'}
                                            </td>
                                            <td className="px-5 py-3 text-sm text-gray-700">
                                                {row.user?.name || '---'}
                                            </td>
                                            <td className="px-5 py-3 text-sm">
                                                <Links row={row} />
                                            </td>
                                            <td className="px-5 py-3 text-sm">
                                                <span
                                                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${status.className}`}>
                                                    {status.label}
                                                </span>
                                            </td>
                                            {canAdd && (
                                                <td className="px-5 py-3">
                                                    {canCancelCaseExpense(task, row) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setCancelling(row)}
                                                            className="text-gray-400 hover:text-[#B42318] p-1.5 rounded-md hover:bg-[#FEE9EC]"
                                                            title="Cancel expense"
                                                            aria-label="Cancel expense">
                                                            <XCircle size={15} />
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td
                                        colSpan={canAdd ? 7 : 6}
                                        className="px-5 py-10 text-center text-gray-500">
                                        No expenses recorded for this case yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default LegalCaseExpensesPanel;
